import React, { useState, useEffect, useId, useMemo } from 'react';
import { Star } from 'lucide-react';
import { getFeedbacksAdmin } from '../../api/feedbacks';
import useMyCourseIds from '../../hooks/useMyCourseIds';
import Dropdown from '../../components/Dropdown/Dropdown';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import PageHead from '../../components/PageHead/PageHead';
import Pagination from '../../components/Pagination/Pagination';
import SearchInput from '../../components/SearchInput/SearchInput';
import { initialsOf, avatarTone } from '../../components/Avatar/Avatar';
import { formatDateTime } from '../../utils/datetime';
import errorText from '../../utils/errorText';
import { pluralKey } from '../../utils/plural';
import { useI18n } from '../../i18n/I18nContext';
import './Feedback.css';

/* ── Helpers ───────────────────────────────────────────────────────── */

// GET /feedbacks/admin checks every `courseIds` entry with class-validator's IsUUID('all'):
// versions 1-8 (plus the nil and max ids). One entry that is not a UUID, such as a legacy
// "rus-tili-course" slug id, fails the whole request with a 400, so only ids the API accepts are sent.
const API_UUID = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|0{8}-0{4}-0{4}-0{4}-0{12}|f{8}-f{4}-f{4}-f{4}-f{12})$/i;
const isApiUuid = (id) => typeof id === 'string' && API_UUID.test(id);

// A review longer than this is clamped to four lines with a "read more" under it.
const LONG_REVIEW_CHARS = 180;

function Stars({ value = 0, size = 15 }) {
  const { t } = useI18n();
  return (
    <span className="fb-stars" role="img" aria-label={t('feedback.outOf5', { value })}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          aria-hidden="true"
          className={n <= Math.round(value) ? 'is-on' : 'is-off'}
          fill={n <= Math.round(value) ? 'currentColor' : 'none'}
        />
      ))}
    </span>
  );
}

// One review. A long one is clamped, and the agent can open it: nothing is hidden for good.
function ReviewCard({ review, course }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const name = review.user?.name || t('common.unknown');
  const email = review.user?.email || '';
  const text = review.content || review.title || '—';
  const long = text.length > LONG_REVIEW_CHARS;

  return (
    <article className="section-card fb-card">
      <header className="fb-card-head">
        <span className="ui-avatar" data-tone={avatarTone(email || name)} aria-hidden="true">{initialsOf(name)}</span>
        <div className="fb-card-meta">
          <span className="fb-card-name" title={name}>{name}</span>
          {email && <span className="fb-card-email" title={email}>{email}</span>}
        </div>
        {review.rating != null && <Stars value={review.rating} />}
      </header>
      <p className={`fb-card-text${long && !open ? ' is-clamped' : ''}`}>{text}</p>
      {long && (
        <button type="button" className="fb-more hit-44" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? t('messaging.feedbackReadLess') : t('messaging.feedbackReadMore')}
        </button>
      )}
      <footer className="fb-card-foot">
        {course ? <span className="ds-badge ds-badge-neutral fb-course-tag" title={course}>{course}</span> : <span />}
        <time className="fb-card-date" dateTime={review.createdAt}>
          {review.createdAt ? formatDateTime(review.createdAt) : ''}
        </time>
      </footer>
    </article>
  );
}

// The page's own loading shape: the overview card, then review cards.
function FeedbackSkeleton({ label }) {
  return (
    <div className="fb-skel" role="status" aria-busy="true" aria-label={label}>
      <div className="section-card fb-skel-summary" aria-hidden="true">
        <div className="ui-skel fb-skel-score" />
        <div className="fb-skel-bars">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="ui-skel fb-skel-bar" />)}
        </div>
      </div>
      <div className="card-grid" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="section-card fb-skel-card">
            <div className="fb-skel-head">
              <div className="ui-skel fb-skel-avatar" />
              <div className="fb-skel-lines">
                <div className="ui-skel fb-skel-line" />
                <div className="ui-skel fb-skel-line is-short" />
              </div>
            </div>
            <div className="ui-skel fb-skel-line" />
            <div className="ui-skel fb-skel-line is-mid" />
          </div>
        ))}
      </div>
      <span className="visually-hidden">{label}</span>
    </div>
  );
}

/* ── Component ─────────────────────────────────────────────────────── */

const REVIEWS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;
// The sort options, as the API's sortBy / sortOrder.
const SORTS = {
  newest: { sortBy: 'createdAt', sortOrder: 'desc' },
  highest: { sortBy: 'rating', sortOrder: 'desc' },
  lowest: { sortBy: 'rating', sortOrder: 'asc' },
};

function Feedback({ embedded = false }) {
  const { t, lang } = useI18n();
  const { courseIds, courses, loading: scopeLoading, error: scopeError } = useMyCourseIds();
  const [feedbacks, setFeedbacks] = useState([]); // the page on screen
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  // Average and star counts over EVERY review in scope, from the server: the page on
  // screen is only a slice, so counting it would misreport both.
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [starFilter, setStarFilter] = useState(0); // 0 = all
  const [sort, setSort] = useState('newest'); // newest | highest | lowest

  const courseParam = useMemo(
    () => (courseIds ? [...courseIds].filter(isApiUuid).join(',') : ''),
    [courseIds],
  );
  const courseTitles = useMemo(
    () => new Map((courses || []).map((c) => [c.id, c.title])),
    [courses],
  );

  // The page belongs to one set of filters: when any of them changes it starts over at 1, set
  // while rendering so no request goes out for the old page first, and switching a filter back
  // does not bring the old page back.
  const filterKey = [courseParam, sort, starFilter, debouncedSearch].join('|');
  const [pageState, setPageState] = useState({ key: filterKey, page: 1 });
  if (pageState.key !== filterKey) setPageState({ key: filterKey, page: 1 });
  const page = pageState.key === filterKey ? pageState.page : 1;
  const setPage = (next) => setPageState({ key: filterKey, page: next });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    if (scopeLoading) return undefined;
    if (!courseParam) {
      // No courses in scope, so no reviews to ask for.
      setFeedbacks([]);
      setSummary(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    getFeedbacksAdmin({
      page,
      limit: REVIEWS_PER_PAGE,
      courseIds: courseParam,
      ...SORTS[sort],
      ...(starFilter ? { rating: starFilter } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        setFeedbacks(res.items || res.data || []);
        setMeta({ total: 0, totalPages: 1, ...res.meta });
        setSummary(res.summary || null);
      })
      // The API client words some failures itself, already in the UI language (messageKey);
      // anything else is the backend's own English text ("each value in courseIds must be a
      // UUID"), which never goes on screen: the page's own line says what failed instead.
      .catch((err) => { if (!cancelled) setError({ text: errorText(err, null) }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scopeLoading, courseParam, page, sort, starFilter, debouncedSearch, reloadKey, t]);

  const retry = () => setReloadKey((n) => n + 1);
  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStarFilter(0);
  };

  const dist = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: summary?.ratingDistribution?.[star] || 0,
  }));
  const ratedCount = summary?.totalReviews || 0;
  const avg = summary?.averageRating || 0;
  const filtered = Boolean(starFilter || debouncedSearch);

  const sortLabelId = useId();
  const sortOptions = [
    { value: 'newest', label: t('messaging.feedbackSortNewest') },
    { value: 'highest', label: t('messaging.feedbackSortHighest') },
    { value: 'lowest', label: t('messaging.feedbackSortLowest') },
  ];

  let body;
  if (scopeLoading || (loading && !summary && !error)) {
    body = <FeedbackSkeleton label={t('messaging.loading')} />;
  } else if ((error || scopeError) && !summary) {
    // Nothing to show yet: say so (an empty state here would claim there are no reviews). When even
    // the list of courses would not load there is nothing to ask the reviews about: starting over
    // is a page reload, as the course list is read once.
    body = (
      <div className="fb-error">
        <PageEmptyState
          variant="error"
          title={t('messaging.feedbackLoadFailed')}
          description={error?.text || undefined}
          onRetry={error ? retry : () => window.location.reload()}
        />
      </div>
    );
  } else if (ratedCount === 0) {
    body = (
      <PageEmptyState
        kind="reviews"
        title={t('feedback.emptyTitle')}
        description={t('feedback.emptyDesc')}
      />
    );
  } else {
    body = (
      <>
        {error && (
          <div className="ui-alert fb-error" role="alert">
            <div>{error.text || t('messaging.feedbackLoadFailed')}</div>
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={retry}>
              {t('ui.retryAgain')}
            </button>
          </div>
        )}

        {/* Overview: the average, and how the stars are spread over every review in scope */}
        <section className="section-card fb-summary">
          <div className="fb-summary-score">
            <div className="fb-avg">{avg.toFixed(1)}</div>
            <Stars value={avg} size={18} />
            <div className="fb-avg-sub">
              {t(pluralKey('messaging.feedbackRatingsCount', ratedCount, lang), { count: ratedCount })}
            </div>
          </div>
          <div className="fb-dist">
            {dist.map((d) => {
              const pct = ratedCount ? (d.count / ratedCount) * 100 : 0;
              return (
                <div
                  key={d.star}
                  className="fb-dist-row"
                  role="img"
                  aria-label={`${t('messaging.feedbackStars', { count: d.star })}: ${d.count}`}
                >
                  <span className="fb-dist-star">{d.star}<Star size={12} fill="currentColor" aria-hidden="true" /></span>
                  <span className="fb-dist-track"><span className="fb-dist-fill" style={{ width: `${pct}%` }} /></span>
                  <span className="fb-dist-count">{d.count}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Filters: search, sort, and the stars as chips */}
        <div className="fb-filters">
          <div className="fb-toolbar">
            <SearchInput
              className="fb-search"
              value={search}
              onChange={setSearch}
              placeholder={t('messaging.feedbackSearchPlaceholder')}
            />
            <div className="fb-sort">
              <span className="fb-sort-label" id={sortLabelId}>{t('messaging.feedbackSortLabel')}</span>
              <Dropdown
                size="sm"
                value={sort}
                onChange={setSort}
                options={sortOptions}
                labelledBy={sortLabelId}
              />
            </div>
          </div>

          <div className="ui-chip-row fb-chips" role="group" aria-label={t('messaging.feedbackFilterAria')}>
            <button type="button" className="ui-chip fb-chip" aria-pressed={starFilter === 0} onClick={() => setStarFilter(0)}>
              {t('feedback.all')}
              <span className="ui-count">{ratedCount}</span>
            </button>
            {dist.map(({ star, count }) => {
              if (!count) return null;
              return (
                <button
                  key={star}
                  type="button"
                  className="ui-chip fb-chip"
                  aria-pressed={starFilter === star}
                  aria-label={`${t('messaging.feedbackStars', { count: star })}: ${count}`}
                  onClick={() => setStarFilter((s) => (s === star ? 0 : star))}
                >
                  {star}<Star size={14} fill="currentColor" aria-hidden="true" />
                  <span className="ui-count">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Reviews */}
        {feedbacks.length === 0 && !loading ? (
          <PageEmptyState
            variant="search"
            size="sm"
            title={t('messaging.feedbackNoMatchTitle')}
            description={t('messaging.feedbackNoMatchHint')}
            actionLabel={filtered ? t('messaging.feedbackClearFilters') : undefined}
            onAction={filtered ? clearFilters : undefined}
          />
        ) : (
          <div className={`card-grid fb-list${loading ? ' is-busy' : ''}`} aria-busy={loading}>
            {feedbacks.map((f) => (
              <ReviewCard
                key={f.id}
                review={f}
                course={f.course?.title || courseTitles.get(f.courseId)}
              />
            ))}
          </div>
        )}

        <Pagination
          page={page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={REVIEWS_PER_PAGE}
          onPrev={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
          hideOnSinglePage
        />
      </>
    );
  }

  return (
    <div className={`fb-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && <PageHead title={t('feedback.title')} description={t('feedback.subtitle')} />}
      {body}
    </div>
  );
}

export default Feedback;
